/**
 * Side cart drawer. Uses Shopify's Section Rendering API: every cart change
 * (qty update, remove, or an add-to-cart triggered elsewhere on the site)
 * re-fetches this component's own section (sections/cart-drawer.liquid) and
 * swaps in the freshly-rendered HTML, instead of rebuilding the item list by
 * hand in JS. Money formatting, translations and preorder detection all
 * happen in Liquid — this file only wires up interactions and syncs the
 * couple of cart-count/total indicators that live outside the drawer, in the
 * header (a different section — snippets/block-cart-icon.liquid).
 */

interface CartItem {
  id: number;
  quantity: number;
}

interface Cart {
  item_count: number;
  total_price: number;
  items: CartItem[];
}

interface CartMutationResponse extends Cart {
  sections?: Record<string, string>;
}

interface ThemeGlobal {
  Currency?: { formatMoney: (price: number, format: string) => string };
  moneyFormat?: string;
  freeShipping?: { load: (cart: Cart) => void };
  updateCurrencies?: () => void;
}

declare global {
  interface Window {
    theme?: ThemeGlobal;
  }
}

export {};

const SECTION_ID = 'cart-drawer';
const CART_JSON_URL = '/cart.js';
const CART_CHANGE_URL = '/cart/change.js';
const CART_UPDATE_URL = '/cart/update.js';

function jsonHeaders(): HeadersInit {
  return { 'Content-Type': 'application/json', Accept: 'application/json' };
}

class CartDrawer extends HTMLElement {
  private requestToken = 0;

  connectedCallback(): void {
    this.bindEvents();
  }

  private get styleCart(): string {
    return this.dataset.cartmini ?? '';
  }

  private bindEvents(): void {
    // The toggle button lives in the header (snippets/block-cart-icon.liquid),
    // a different section than this drawer's own — listen at the document level.
    document.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('.js-toggle-cart')) {
        this.classList.toggle('active');
      }
    });

    // Delegated on `this`, not on specific nodes: every cart change replaces
    // this element's children wholesale (see applySection), so a listener
    // bound directly to e.g. the close button would stop working after the
    // first refresh.
    this.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      if (target.closest('.pdm-close__button')) {
        this.classList.toggle('active');
        return;
      }

      if (target.closest('.overlaycart')) {
        this.classList.remove('active');
        return;
      }

      const noteTitle = target.closest<HTMLElement>('.cart-note__title');
      if (noteTitle) {
        this.toggleCartNote(noteTitle);
        return;
      }

      if (target.classList.contains('qty-increase')) {
        const id = target.dataset.id;
        if (id) this.run(this.changeQuantity(id, 1));
        return;
      }

      if (target.classList.contains('qty-decrease')) {
        const id = target.dataset.id;
        if (!id) return;

        const input = target.closest('.mini-cart-item')?.querySelector<HTMLInputElement>('.qty-input');
        const currentQty = input ? parseInt(input.value, 10) : 0;

        if (currentQty - 1 <= 0) {
          this.run(this.removeItem(id));
        } else {
          this.run(this.changeQuantity(id, -1));
        }
      }
    });

    this.addEventListener('keydown', (event) => {
      const target = event.target as HTMLElement;
      if (!target.classList.contains('qty-input') || event.key !== 'Enter') return;

      event.preventDefault();
      this.applyQtyInputChange(target as HTMLInputElement);
    });

    document.addEventListener('cart:refresh', () => this.run(this.refresh()));
  }

  // Fire-and-forget helper for click/event handlers, which can't themselves
  // be async — logs instead of letting failures vanish as unhandled
  // rejections (this is exactly the kind of failure that looks like "nothing
  // happened" from the outside, e.g. an add-to-cart elsewhere on the site
  // not refreshing the drawer).
  private run(promise: Promise<void>): void {
    promise.catch((error) => console.error('[cart-drawer]', error));
  }

  private toggleCartNote(title: HTMLElement): void {
    const box = this.querySelector<HTMLElement>('#specialInstructionBox');
    title.classList.toggle('active');
    if (box) box.style.display = title.classList.contains('active') ? 'block' : 'none';
  }

  private applyQtyInputChange(input: HTMLInputElement): void {
    const increaseBtn = input.closest('.mini-cart-item')?.querySelector<HTMLElement>('.qty-increase');
    const id = increaseBtn?.dataset.id;
    if (!id) return;

    const value = parseInt(input.value, 10);
    if (isNaN(value) || value < 1) {
      this.run(this.removeItem(id));
      return;
    }

    this.run(this.changeQuantity(id, value, true));
  }

  private async fetchCart(): Promise<Cart> {
    const response = await fetch(CART_JSON_URL, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    return response.json();
  }

  // Deliberately NOT the general `GET <path>?sections=...` endpoint. That one
  // renders through the full page pipeline and, confirmed live, can lag a
  // step behind `/cart.js` — e.g. after an add-to-cart elsewhere on the site,
  // it would sometimes render the line item at its *previous* quantity even
  // though `/cart.js` already reported the updated one. `/cart/update.js`
  // with an empty `updates` is a no-op cart mutation, but it's still a cart
  // mutation — Shopify returns the cart JSON and the rendered section HTML
  // from the exact same atomic read, so the two can never disagree.
  private async fetchCartAndSection(): Promise<CartMutationResponse> {
    const response = await fetch(CART_UPDATE_URL, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ updates: {}, sections: SECTION_ID }),
    });
    return response.json();
  }

  private async changeQuantity(id: string, change: number, absolute = false): Promise<void> {
    const token = ++this.requestToken;
    const cart = await this.fetchCart();
    const line = cart.items.findIndex((item) => String(item.id) === id);
    if (line === -1) return;

    const currentQty = cart.items[line]?.quantity ?? 0;
    const newQty = Math.max(1, absolute ? change : currentQty + change);

    const response = await fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ line: line + 1, quantity: newQty, sections: SECTION_ID }),
    });
    const updated: CartMutationResponse = await response.json();
    this.commit(token, updated, updated.sections?.[SECTION_ID] ?? '');
  }

  private async removeItem(id: string): Promise<void> {
    const token = ++this.requestToken;
    const response = await fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ id, quantity: 0, sections: SECTION_ID }),
    });
    const updated: CartMutationResponse = await response.json();
    this.commit(token, updated, updated.sections?.[SECTION_ID] ?? '');
  }

  async refresh(): Promise<void> {
    const token = ++this.requestToken;
    const updated = await this.fetchCartAndSection();
    this.commit(token, updated, updated.sections?.[SECTION_ID] ?? '');
  }

  // Several cart mutations can be in flight at once — an add-to-cart
  // elsewhere on the site (AjaxCart fires the same "refresh" event twice per
  // add) racing this drawer's own qty change, for instance — and Shopify's
  // responses can resolve out of order. We confirmed this live: the request
  // that *finished* last was winning even when it had *started* first, so a
  // just-added item would flash into the cart and then disappear again as an
  // older, staler response landed afterwards. Only ever apply the result
  // belonging to the most recently *started* request; drop anything else.
  private commit(token: number, cart: Cart, sectionHtml: string): void {
    if (token !== this.requestToken) return;
    this.render(cart, sectionHtml);
  }

  private render(cart: Cart, sectionHtml: string): void {
    this.applySection(sectionHtml);
    this.syncHeaderIndicators(cart);
    window.theme?.freeShipping?.load(cart);
    window.theme?.updateCurrencies?.();
  }

  private applySection(html: string): void {
    if (!html) return;

    // .bar-level animates its own `width` via a CSS transition (see
    // pdm-custom-styles.css). Swapping in a whole new element (below) means
    // the new node's initial width is just however it renders — nothing to
    // transition *from* — so the bar would otherwise snap instead of
    // sliding. Capture the old width, force the new node to start there,
    // then let it transition to its real target on the next frame.
    const previousBarWidth = this.querySelector<HTMLElement>('.bar-level')?.style.width;

    const template = document.createElement('template');
    template.innerHTML = html;
    const fresh = template.content.querySelector('cart-drawer');
    if (!fresh) return;

    this.innerHTML = fresh.innerHTML;

    if (previousBarWidth) {
      const bar = this.querySelector<HTMLElement>('.bar-level');
      if (bar) {
        const targetWidth = bar.style.width;
        bar.style.transition = 'none';
        bar.style.width = previousBarWidth;
        bar.offsetHeight; // force a reflow so the "from" width above actually commits before...
        requestAnimationFrame(() => {
          bar.style.transition = '';
          bar.style.width = targetWidth; // ...this "to" width transitions instead of snapping.
        });
      }
    }
  }

  // .js-cart-count/.js-cart-total/.js-cart-btn-checkout live in the header
  // icon (snippets/block-cart-icon.liquid), a different section than this
  // drawer, so they're synced globally by class instead of via querySelector.
  private syncHeaderIndicators(cart: Cart): void {
    document.querySelectorAll('.js-cart-count').forEach((el) => {
      el.textContent = String(cart.item_count);
    });

    document.querySelectorAll<HTMLElement>('.js-cart-btn-checkout').forEach((btn) => {
      btn.classList.toggle('disabled', cart.item_count === 0);
    });

    const formattedTotal = window.theme?.Currency?.formatMoney(cart.total_price, window.theme?.moneyFormat ?? '') ?? '';
    document.querySelectorAll('.js-cart-total').forEach((el) => {
      el.innerHTML = formattedTotal;
    });

    // A cart:refresh on the cart page itself (e.g. updating a line item's
    // quantity there) should still sync these header indicators, but must
    // NOT force the drawer open — the shopper is already looking at their
    // cart. Elsewhere (add-to-cart, quickview, etc.) opening on refresh is
    // the intended behavior, since there's no separate explicit "open" call.
    if (this.styleCart === 'true' && !document.body.classList.contains('template-cart')) {
      this.classList.toggle('active', cart.item_count > 0);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
